import { defineIntegration, type IntegrationFn } from '@sentry/core';
import { type AnyRouter } from '@tanstack/react-router';
import { isErrorWithSentryContext } from '@/api/errors';
import { deriveApiFallbackTags, deriveRouteFallbackTags, getFetchRelatedErrorFingerprint } from '@/api/sentry-context';

const INTEGRATION_NAME = 'QdrantSentryContext';

/**
 * Enriches every outgoing event with our canonical tag taxonomy, isolated from `beforeSend` so that
 * hook is left with only the drop decisions. It runs in `preprocessEvent` (the mutate-only stage
 * that fires before `beforeSend`) mirroring Sentry's own `linkedErrorsIntegration`.
 * @see https://github.com/getsentry/sentry-javascript/blob/master/packages/core/src/integrations/linkederrors.ts
 *
 * It performs, in order, and the order encodes the `domain`/`route.id` precedence
 * (explicit `meta.sentryContext` > API-derived (Connect key) > route `staticData` fallback):
 *  1. Merges the per-error `meta.sentryContext` (explicit tags/extra/level) onto the event.
 *  2. Backfills `route.id`/`domain` from the matched route for events that lack them.
 *  3. Computes an endpoint-based fingerprint for API errors.
 *
 * The `ignore` flag is intentionally left to `beforeSend` (the only hook that can drop an event) so
 * an ignored error is passed through untouched here, with its context left intact for that drop.
 */
const _sentryContextIntegration = ((router: AnyRouter) => ({
  name: INTEGRATION_NAME,
  preprocessEvent(event, hint) {
    // Only enrich error/message events (`type` is undefined), matching the scope `beforeSend` had;
    // transactions are handled separately by `beforeSendTransaction`.
    if (event.type) {
      return;
    }

    const originalException = hint?.originalException;
    // If the error has additional Sentry context, merge it into the event.
    if (isErrorWithSentryContext(originalException) && originalException.sentryContext) {
      // Leave ignored errors (and their context) untouched for `beforeSend` to drop.
      if (originalException.sentryContext.ignore) {
        return;
      }

      const { tags, extra, level } = originalException.sentryContext;
      event.tags = { ...event.tags, ...tags };
      event.extra = { ...event.extra, ...extra };

      if (level) {
        event.level = level;
      }

      originalException.sentryContext = undefined;
    }

    // Keep the backend trace id on every API error, including the ones captured outside the
    // QueryClient choke point (manual reports, error boundaries, route resolvers).
    event.tags = deriveApiFallbackTags(event.tags, originalException);
    // Make every event (runtime errors included) filterable by route and owner `domain`, not only the
    // API errors enriched at the QueryClient choke point — values captured there at error time win.
    event.tags = deriveRouteFallbackTags(event.tags, router);
    // Group API errors by what failed (endpoint × failure class) instead of by stack trace.
    const fingerprint = getFetchRelatedErrorFingerprint(originalException, event.tags);
    if (fingerprint) {
      event.fingerprint = fingerprint;
    }
  },
})) satisfies IntegrationFn;

/**
 * Sentry integration that attaches Qdrant's canonical tag taxonomy and fingerprints to every event.
 * Pass the app router so route-derived fallbacks can read the matched routes at capture time.
 */
export const sentryContextIntegration = defineIntegration(_sentryContextIntegration);
